import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Package, Warning, ClockCounterClockwise, Bell, Tractor,
  Storefront, X, MapPin, Basket, Sparkle, Bicycle, Buildings, Download,
} from "@phosphor-icons/react";

/**
 * InventoryApp — the generic Universal Inventory application.
 *
 * A brand-agnostic implementation of the Universal Inventory manager that
 * powers Farmers Pulse, but is designed to be embedded on ANY site in the
 * SocialPulse ecosystem (NaturePulse, PhotoPulse, TravelerPulse, etc.).
 *
 * Guiding principle:
 *   Add Anything → Know Where It Is → Know Its Condition →
 *   Know When It Needs Attention → Sell or Share When Needed.
 *
 * Props:
 *   context     — module tag (e.g. "farmers", "nature") for API scoping/analytics
 *   brand       — brand label ("Farmers Pulse", "NaturePulse", …)
 *   accent      — brand hex ("#B85F2A")
 *   backLabel   — text for the top back-link
 *   backHref    — URL for the top back-link
 *   navSlot     — optional React node rendered above the header (site nav)
 *   sellCategories — array of categories used when "Sell on shop"
 *   sellTargetLabel — CTA label ("List on shop", "Post to marketplace")
 *   apiPrefix   — inventory API prefix (default "/farmers/inventory")
 */

const ALERT_LABELS = {
  "critical-stock":    { label: "Critical stock", color: "bg-red-100 text-red-800" },
  "low-stock":         { label: "Low stock",      color: "bg-orange-100 text-orange-800" },
  "reorder-needed":    { label: "Reorder",        color: "bg-amber-100 text-amber-800" },
  "overstock":         { label: "Overstock",      color: "bg-blue-100 text-blue-800" },
  "expiring-imminent": { label: "Expires in 3d",  color: "bg-red-100 text-red-800" },
  "expiring-soon":     { label: "Expiring soon",  color: "bg-amber-100 text-amber-800" },
  "expired":           { label: "Expired",        color: "bg-red-100 text-red-800" },
  "needs-attention":   { label: "Needs repair",   color: "bg-red-100 text-red-800" },
  "missing":           { label: "Missing",        color: "bg-red-100 text-red-800" },
};

const CATEGORY_ICONS = {
  "produce":      Basket,
  "crops-plants": Sparkle,
  "farm-inputs":  Package,
  "tools":        Tractor,
  "vehicles":     Bicycle,
  "buildings":    Buildings,
  "office-tech":  Package,
  "hospitality":  Buildings,
  "dining":       Basket,
  "other":        Package,
};

export default function InventoryApp({
  context = "farmers",
  brand = "Farmers Pulse",
  accent = "#B85F2A",
  backLabel = "My Farm",
  backHref = "/farmers/mine",
  navSlot = null,
  sellCategories = ["vegetables", "fruits", "berries", "herbs", "eggs", "dairy", "honey",
    "meat", "poultry", "preserves", "baked", "flowers", "plants", "seedlings", "other"],
  sellTargetLabel = "List on shop",
  apiPrefix = "/farmers/inventory",
}) {
  const { user, requireSignIn } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [attention, setAttention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("attention");
  const [filters, setFilters] = useState({ top_category: "", location_id: "", q: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showAddLocation, setShowAddLocation] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, locs, its, att] = await Promise.all([
        http.get(`${apiPrefix}/catalog`).then((r) => r.data),
        http.get(`${apiPrefix}/locations`).then((r) => r.data.locations),
        http.get(`${apiPrefix}/items`, { params: { limit: 500 } }).then((r) => r.data.items),
        http.get(`${apiPrefix}/attention`).then((r) => r.data),
      ]);
      setCatalog(cat);
      setLocations(locs);
      setItems(its);
      setAttention(att);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load inventory");
    } finally { setLoading(false); }
  }, [apiPrefix]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const filteredItems = useMemo(() => {
    return items.filter((i) => {
      if (filters.top_category && i.top_category !== filters.top_category) return false;
      if (filters.location_id && i.location_id !== filters.location_id) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const blob = [i.name, i.description, i.model, i.manufacturer, i.serial_number, i.sku, i.vin, ...(i.tags || [])].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        {navSlot}
        <div className="max-w-2xl mx-auto text-center py-20">
          <Package size={40} weight="duotone" className="mx-auto" style={{ color: accent }} />
          <h1 className="mt-4 font-serif-display text-4xl">Sign in to open your inventory</h1>
          <p className="mt-2 text-[#3A3830]">Your inventory is private and tied to your account.</p>
          <button onClick={requireSignIn}
            className="mt-6 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 text-sm"
            data-testid="inv-signin-cta">Sign in to continue</button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 pb-24 px-5 md:px-10"
      data-testid="inventory-app" data-inventory-context={context}>
      {navSlot}
      <div className="max-w-7xl mx-auto">
        <Link to={backHref} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> {backLabel}
        </Link>

        <header className="mt-3 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase flex items-center gap-1"
              style={{ color: accent }}>
              <Package size={12} weight="fill" /> {brand} · Universal Inventory
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              One workspace. Everything tracked.
            </h1>
            <p className="mt-3 text-[#3A3830] max-w-2xl">
              From carrots to tractors, from farm-stand fixtures to farm-stay furniture — track what you have, where it is, its condition, and what needs your attention.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowAddLocation(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-2 text-sm"
              data-testid="inv-add-location">
              <MapPin size={12} weight="fill" /> Add location
            </button>
            <a href={`/api${apiPrefix}/export.csv`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-2 text-sm"
              data-testid="inv-export-csv">
              <Download size={12} weight="fill" /> CSV
            </a>
            <button onClick={() => setShowAdd(true)}
              style={{ background: accent }}
              className="inline-flex items-center gap-1.5 rounded-full text-white px-4 py-2 text-sm font-medium"
              data-testid="inv-add-anything">
              <Plus size={12} weight="bold" /> Add anything
            </button>
          </div>
        </header>

        <div className="mt-8 flex items-center gap-2 flex-wrap">
          <TabBtn on={tab === "attention"} onClick={() => setTab("attention")}
            icon={Bell} accent={accent}
            count={
              attention?.totals
                ? (attention.totals.expiring + attention.totals.expired + attention.totals.critical +
                   attention.totals.low + attention.totals.reorder + attention.totals.needs_attention +
                   attention.totals.missing + attention.totals.reminders_due)
                : 0
            }>
            Attention
          </TabBtn>
          <TabBtn on={tab === "items"} onClick={() => setTab("items")} icon={Package} accent={accent} count={items.length}>
            Items
          </TabBtn>
          <TabBtn on={tab === "locations"} onClick={() => setTab("locations")} icon={MapPin} accent={accent} count={locations.length}>
            Locations
          </TabBtn>
        </div>

        {loading ? (
          <div className="mt-8 rounded-2xl border border-black/10 bg-white p-8 text-center text-sm text-[#3A3830]">
            Loading your inventory…
          </div>
        ) : (
          <div className="mt-6">
            {tab === "attention" && attention && <AttentionCenter attention={attention} onSelect={setDetail} onRefresh={load} apiPrefix={apiPrefix} />}
            {tab === "items" && (
              <ItemsList items={filteredItems} allItems={items} catalog={catalog} locations={locations}
                filters={filters} setFilters={setFilters} onSelect={setDetail} onAdd={() => setShowAdd(true)} accent={accent} />
            )}
            {tab === "locations" && (
              <LocationsList locations={locations} items={items} onAdd={() => setShowAddLocation(true)} onRefresh={load} accent={accent} />
            )}
          </div>
        )}
      </div>

      {showAdd && catalog && (
        <AddItemModal catalog={catalog} locations={locations} accent={accent} apiPrefix={apiPrefix}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}
      {showAddLocation && (
        <AddLocationModal onClose={() => setShowAddLocation(false)} apiPrefix={apiPrefix} accent={accent}
          onSaved={() => { setShowAddLocation(false); load(); }} />
      )}
      {detail && (
        <ItemDetailModal itemId={detail.id} catalog={catalog} locations={locations} accent={accent}
          apiPrefix={apiPrefix} sellCategories={sellCategories} sellTargetLabel={sellTargetLabel}
          onClose={() => setDetail(null)} onChanged={load} />
      )}
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */
function TabBtn({ on, onClick, icon: Icon, count, children, accent = "#B85F2A" }) {
  return (
    <button onClick={onClick}
      style={on ? undefined : {}}
      className={`rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5 ${on ? "bg-[#1B4332] text-[#F5EBDC]" : "bg-white border border-black/15 hover:border-current"}`}
      data-testid={`inv-tab-${(children || "").toString().toLowerCase()}`}>
      <Icon size={12} weight="fill" /> {children}
      {count > 0 && (
        <span className={`rounded-full px-1.5 text-[10px] font-mono ${on ? "bg-[#F5EBDC] text-[#1B4332]" : "text-white"}`}
          style={on ? undefined : { background: accent }}>
          {count}
        </span>
      )}
    </button>
  );
}

function AlertPill({ tag }) {
  const meta = ALERT_LABELS[tag];
  if (!meta) return null;
  return <span className={`text-[10px] uppercase tracking-[0.22em] font-mono rounded-full px-2 py-0.5 ${meta.color}`}>{meta.label}</span>;
}

function AttentionCenter({ attention, onSelect, onRefresh, apiPrefix }) {
  const groups = [
    { key: "expiring", label: "Expiring soon", items: attention.expiring, icon: ClockCounterClockwise, color: "text-amber-700 bg-amber-50" },
    { key: "expired", label: "Expired",       items: attention.expired,  icon: Warning, color: "text-red-700 bg-red-50" },
    { key: "critical", label: "Critical stock", items: attention.critical, icon: Warning, color: "text-red-700 bg-red-50" },
    { key: "low", label: "Low stock",         items: attention.low,      icon: Warning, color: "text-orange-700 bg-orange-50" },
    { key: "reorder", label: "Reorder needed", items: attention.reorder, icon: Package, color: "text-amber-700 bg-amber-50" },
    { key: "overstock", label: "Overstock",   items: attention.overstock, icon: Warning, color: "text-blue-700 bg-blue-50" },
    { key: "needs_attention", label: "Needs repair", items: attention.needs_attention, icon: Warning, color: "text-red-700 bg-red-50" },
    { key: "missing", label: "Missing",       items: attention.missing,  icon: Warning, color: "text-red-700 bg-red-50" },
  ];
  const hasSomething = groups.some((g) => (g.items || []).length > 0) || (attention.reminders_due || []).length > 0;
  return (
    <div className="space-y-4" data-testid="inv-attention-center">
      {!hasSomething && (
        <div className="rounded-2xl border border-black/10 bg-white p-10 text-center">
          <Sparkle size={40} weight="duotone" className="mx-auto text-emerald-500" />
          <h3 className="mt-3 font-serif-display text-2xl">All clear — nothing needs attention.</h3>
          <p className="mt-2 text-sm text-[#3A3830]">Your inventory is stocked, fresh, and up-to-date.</p>
        </div>
      )}

      {(attention.reminders_due || []).length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A] flex items-center gap-1">
            <Bell size={11} weight="fill" /> Reminders due today
          </div>
          <div className="mt-3 space-y-2">
            {attention.reminders_due.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 border-b border-black/5 py-2 last:border-0">
                <div>
                  <div className="text-sm font-medium">{r.title}</div>
                  <div className="text-xs text-[#3A3830]">{r.date} · {r.reminder_type || "custom"}</div>
                </div>
                <button onClick={async () => {
                  await http.post(`${apiPrefix}/reminders/${r.id}/complete`);
                  toast.success("Done"); onRefresh();
                }} className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-1 text-xs" data-testid={`inv-reminder-done-${r.id}`}>
                  Mark done
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.filter((g) => (g.items || []).length > 0).map((g) => (
        <div key={g.key} className="rounded-2xl border border-black/10 bg-white p-5">
          <div className={`text-[10px] uppercase tracking-[0.28em] font-mono flex items-center gap-1 ${g.color}`}>
            <g.icon size={11} weight="fill" /> {g.label} · {g.items.length}
          </div>
          <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {g.items.map((it) => <ItemMiniCard key={it.id} item={it} onClick={() => onSelect(it)} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ItemMiniCard({ item, onClick }) {
  const Icon = CATEGORY_ICONS[item.top_category] || Package;
  const photo = item.photos && item.photos[0];
  return (
    <button onClick={onClick}
      className="text-left rounded-xl border border-black/10 hover:border-current p-3 flex items-center gap-3"
      data-testid={`inv-item-${item.id}`}>
      <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden shrink-0">
        {photo ? <img src={photo} className="w-full h-full object-cover" alt="" /> : <Icon size={16} weight="duotone" className="text-[#3A3830]" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{item.name}</div>
        <div className="text-xs text-[#3A3830]">
          {item.quantity} {item.unit} · {item.top_category}
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        {(item._alerts || []).slice(0, 2).map((a) => <AlertPill key={a} tag={a} />)}
      </div>
    </button>
  );
}

function ItemsList({ items, allItems, catalog, locations, filters, setFilters, onSelect, onAdd, accent = "#B85F2A" }) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="Search name, serial, VIN, SKU, tag…"
          className="flex-1 min-w-[220px] rounded-full border border-black/15 bg-white px-4 py-2 text-sm outline-none focus:border-current"
          data-testid="inv-search" />
        <select value={filters.top_category}
          onChange={(e) => setFilters({ ...filters, top_category: e.target.value })}
          className="rounded-full border border-black/15 bg-white px-3 py-2 text-sm" data-testid="inv-filter-category">
          <option value="">All categories</option>
          {catalog?.top_categories?.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select value={filters.location_id}
          onChange={(e) => setFilters({ ...filters, location_id: e.target.value })}
          className="rounded-full border border-black/15 bg-white px-3 py-2 text-sm" data-testid="inv-filter-location">
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-black/10 bg-white p-10 text-center">
          <Package size={40} weight="duotone" className="mx-auto text-[#3A3830]" />
          <h3 className="mt-3 font-serif-display text-2xl">
            {allItems.length === 0 ? "Your inventory is empty" : "No items match your filters"}
          </h3>
          <p className="mt-2 text-sm text-[#3A3830]">
            {allItems.length === 0 ? "Add anything — a tractor, a tomato harvest, a cooler, a market truck." : "Try different filters or clear them."}
          </p>
          {allItems.length === 0 && (
            <button onClick={onAdd}
              style={{ background: accent }}
              className="mt-4 rounded-full text-white px-4 py-2 text-sm font-medium">
              <Plus size={12} weight="bold" className="inline mr-1" /> Add my first item
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inv-items-grid">
          {items.map((it) => <ItemMiniCard key={it.id} item={it} onClick={() => onSelect(it)} />)}
        </div>
      )}
    </div>
  );
}

function LocationsList({ locations, items, onAdd, onRefresh, accent = "#B85F2A" }) {
  const countByLoc = useMemo(() => {
    const c = {};
    for (const i of items) if (i.location_id) c[i.location_id] = (c[i.location_id] || 0) + 1;
    return c;
  }, [items]);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      {locations.length === 0 && (
        <div className="col-span-full rounded-2xl border-2 border-dashed border-black/10 p-10 text-center">
          <MapPin size={30} weight="duotone" className="mx-auto" style={{ color: accent }} />
          <p className="mt-3 text-sm text-[#3A3830]">Add your first storage location.</p>
          <button onClick={onAdd} className="mt-3 rounded-full text-white px-4 py-2 text-sm"
            style={{ background: accent }}>
            <Plus size={11} weight="bold" className="inline mr-1" /> Add location
          </button>
        </div>
      )}
      {locations.map((l) => (
        <div key={l.id} className="rounded-2xl border border-black/10 bg-white p-4" data-testid={`inv-loc-${l.id}`}>
          <div className="text-sm font-medium flex items-center gap-1.5">
            <MapPin size={13} weight="fill" style={{ color: accent }} /> {l.name}
          </div>
          <div className="text-xs text-[#3A3830] mt-1">{l.kind || "storage"}</div>
          <div className="text-xs mt-2">
            {countByLoc[l.id] || 0} item{(countByLoc[l.id] || 0) !== 1 ? "s" : ""} here
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add item / add location / item detail modals                       */
/* ------------------------------------------------------------------ */
function ModalShell({ children, onClose, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-3xl w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[88vh] overflow-y-auto shadow-2xl`}>
        {children}
      </div>
    </div>
  );
}

function AddItemModal({ catalog, locations, onClose, onSaved, apiPrefix, accent = "#B85F2A" }) {
  const [form, setForm] = useState({
    name: "", top_category: "produce", subcategory: "",
    quantity: 0, unit: "each",
    critical_level: "", low_level: "", reorder_level: "", target_level: "",
    location_id: "", condition: "good", ownership: "owned", visibility: "private",
    description: "", tags: "",
    manufacturer: "", model: "", serial_number: "", year: "",
    purchase_price: "", best_before_date: "", warranty_expiration: "",
    photos: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cat = catalog?.top_categories?.find((c) => c.id === form.top_category);
  const isProduce = form.top_category === "produce" || form.top_category === "crops-plants" || form.top_category === "farm-inputs";
  const isAsset   = ["tools", "vehicles", "buildings", "office-tech", "hospitality", "dining"].includes(form.top_category);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give it a name.");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(), top_category: form.top_category,
        subcategory: form.subcategory || null,
        description: form.description || null,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        quantity: parseFloat(form.quantity) || 0, unit: form.unit,
        critical_level: form.critical_level ? parseFloat(form.critical_level) : null,
        low_level: form.low_level ? parseFloat(form.low_level) : null,
        reorder_level: form.reorder_level ? parseFloat(form.reorder_level) : null,
        target_level: form.target_level ? parseFloat(form.target_level) : null,
        condition: form.condition, ownership: form.ownership, visibility: form.visibility,
        location_id: form.location_id || null,
        photos: form.photos.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 4),
      };
      if (isAsset) {
        Object.assign(payload, {
          manufacturer: form.manufacturer || null, model: form.model || null,
          serial_number: form.serial_number || null,
          year: form.year ? parseInt(form.year, 10) : null,
          purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
          warranty_expiration: form.warranty_expiration || null,
        });
      }
      if (isProduce) {
        Object.assign(payload, {
          best_before_date: form.best_before_date || null,
        });
      }
      await http.post(`${apiPrefix}/items`, payload);
      toast.success("Item added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} wide>
      <div className="p-6" data-testid="inv-add-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono" style={{ color: accent }}>Universal Inventory</div>
            <h3 className="mt-1 font-serif-display text-3xl">Add anything</h3>
          </div>
          <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <FieldLabel label="Name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Heirloom Tomatoes, John Deere 5045E, Cooler #1…"
              className="input" autoFocus data-testid="inv-add-name" />
          </FieldLabel>
          <FieldLabel label="Category">
            <select value={form.top_category} onChange={(e) => set("top_category", e.target.value)}
              className="input" data-testid="inv-add-category">
              {catalog?.top_categories?.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Subcategory">
            <select value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className="input"
              data-testid="inv-add-subcategory">
              <option value="">— none —</option>
              {(catalog?.default_subcategories?.[form.top_category] || []).map((s) => (
                <option key={s} value={s}>{s.replace(/-/g, " ")}</option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Location">
            <select value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="input" data-testid="inv-add-location-field">
              <option value="">— none —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Quantity">
            <input type="number" min="0" step="0.01" value={form.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              className="input" data-testid="inv-add-qty" />
          </FieldLabel>
          <FieldLabel label="Unit">
            <select value={form.unit} onChange={(e) => set("unit", e.target.value)} className="input"
              data-testid="inv-add-unit">
              {catalog?.units?.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Condition">
            <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className="input"
              data-testid="inv-add-condition">
              <option value="">—</option>
              {catalog?.conditions?.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ")}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Ownership">
            <select value={form.ownership} onChange={(e) => set("ownership", e.target.value)} className="input"
              data-testid="inv-add-ownership">
              {catalog?.ownership_kinds?.map((c) => <option key={c} value={c}>{c.replace(/-/g, " ")}</option>)}
            </select>
          </FieldLabel>
        </div>

        <div className="mt-4 grid md:grid-cols-4 gap-3">
          <FieldLabel label="Critical" hint="alert level">
            <input value={form.critical_level} onChange={(e) => set("critical_level", e.target.value)}
              type="number" min="0" className="input" placeholder="e.g. 5"
              data-testid="inv-add-critical" />
          </FieldLabel>
          <FieldLabel label="Low">
            <input value={form.low_level} onChange={(e) => set("low_level", e.target.value)}
              type="number" min="0" className="input" placeholder="e.g. 10"
              data-testid="inv-add-low" />
          </FieldLabel>
          <FieldLabel label="Reorder">
            <input value={form.reorder_level} onChange={(e) => set("reorder_level", e.target.value)}
              type="number" min="0" className="input" placeholder="e.g. 20"
              data-testid="inv-add-reorder" />
          </FieldLabel>
          <FieldLabel label="Target max">
            <input value={form.target_level} onChange={(e) => set("target_level", e.target.value)}
              type="number" min="0" className="input" placeholder="e.g. 100"
              data-testid="inv-add-target" />
          </FieldLabel>
        </div>

        {isProduce && (
          <div className="mt-4">
            <FieldLabel label="Best-before / expiration date">
              <input type="date" value={form.best_before_date}
                onChange={(e) => set("best_before_date", e.target.value)} className="input" />
            </FieldLabel>
          </div>
        )}

        {isAsset && (
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            <FieldLabel label="Manufacturer">
              <input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} className="input" />
            </FieldLabel>
            <FieldLabel label="Model">
              <input value={form.model} onChange={(e) => set("model", e.target.value)} className="input" />
            </FieldLabel>
            <FieldLabel label="Year">
              <input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className="input" />
            </FieldLabel>
            <FieldLabel label="Serial number">
              <input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} className="input" />
            </FieldLabel>
            <FieldLabel label="Purchase price">
              <input type="number" min="0" step="0.01" value={form.purchase_price}
                onChange={(e) => set("purchase_price", e.target.value)} className="input" placeholder="0.00" />
            </FieldLabel>
            <FieldLabel label="Warranty expires">
              <input type="date" value={form.warranty_expiration}
                onChange={(e) => set("warranty_expiration", e.target.value)} className="input" />
            </FieldLabel>
          </div>
        )}

        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <FieldLabel label="Description">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              rows={2} className="input" />
          </FieldLabel>
          <FieldLabel label="Tags (comma separated)">
            <input value={form.tags} onChange={(e) => set("tags", e.target.value)}
              className="input" placeholder="organic, heirloom, seasonal" />
          </FieldLabel>
        </div>

        <div className="mt-4">
          <FieldLabel label="Photos (comma-separated URLs)" hint="up to 4">
            <input value={form.photos} onChange={(e) => set("photos", e.target.value)}
              className="input font-mono text-[11px]" placeholder="https://…, https://…" />
          </FieldLabel>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy || !form.name}
            style={{ background: accent }}
            className="rounded-full text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="inv-add-save">
            {busy ? "Saving…" : "Add item"}
          </button>
        </div>
      </div>
      <style>{`.input { width: 100%; border-radius: 8px; border: 1px solid rgba(0,0,0,0.15); padding: 6px 10px; font-size: 13px; outline: none; background: white; }
        .input:focus { border-color: ${accent}; }`}</style>
    </ModalShell>
  );
}

function AddLocationModal({ onClose, onSaved, apiPrefix, accent = "#B85F2A" }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("storage");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.post(`${apiPrefix}/locations`, { name: name.trim(), kind });
      toast.success("Location added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose}>
      <div className="p-6" data-testid="inv-add-loc-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl">Add storage location</h3>
          <button onClick={onClose} className="p-1"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <label className="block">
            <div className="text-xs text-[#3A3830]">Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Main Barn, North Cooler, Market Truck…"
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
              autoFocus data-testid="inv-loc-name" />
          </label>
          <label className="block">
            <div className="text-xs text-[#3A3830]">Kind</div>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
              className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm">
              {["farm", "building", "barn", "shed", "cooler", "freezer", "warehouse", "farm-stand", "vehicle", "market", "garage", "workshop", "office", "storage", "custom"].map((k) => (
                <option key={k} value={k}>{k.replace(/-/g, " ")}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-black/15 px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={busy || !name.trim()}
            style={{ background: accent }}
            className="rounded-full text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="inv-loc-save">
            {busy ? "Saving…" : "Add location"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ItemDetailModal({ itemId, catalog, locations, onClose, onChanged, apiPrefix, accent = "#B85F2A", sellCategories, sellTargetLabel = "List on shop" }) {
  const [item, setItem] = useState(null);
  const [txns, setTxns] = useState([]);
  const [tab, setTab] = useState("overview");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [sellCat, setSellCat] = useState((sellCategories && sellCategories[0]) || "vegetables");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [it, tx] = await Promise.all([
      http.get(`${apiPrefix}/items/${itemId}`).then((r) => r.data.item),
      http.get(`${apiPrefix}/items/${itemId}/transactions`).then((r) => r.data.transactions),
    ]);
    setItem(it); setTxns(tx);
  }, [itemId, apiPrefix]);

  useEffect(() => { load(); }, [load]);

  if (!item) return (
    <ModalShell onClose={onClose}>
      <div className="p-10 text-center text-sm text-[#3A3830]">Loading…</div>
    </ModalShell>
  );

  const adjust = async () => {
    if (!delta) return;
    setBusy(true);
    try {
      await http.post(`${apiPrefix}/items/${itemId}/adjust`, {
        delta: parseFloat(delta), reason: reason || null,
      });
      setDelta(""); setReason("");
      await load(); onChanged();
      toast.success("Adjusted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await http.post(`${apiPrefix}/items/${itemId}/notes`, { text: note.trim() });
    setNote(""); await load(); toast.success("Note added");
  };

  const sell = async () => {
    if (!sellPrice) return toast.error("Set a price");
    setBusy(true);
    try {
      await http.post(`${apiPrefix}/items/${itemId}/sell`, {
        price: parseFloat(sellPrice), product_category: sellCat,
      });
      toast.success("Listed on your shop!");
      await load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't list"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${item.name}"? History stays for audit.`)) return;
    await http.delete(`${apiPrefix}/items/${itemId}`);
    toast.success("Archived"); onClose(); onChanged();
  };

  return (
    <ModalShell onClose={onClose} wide>
      <div className="p-6" data-testid="inv-detail-modal">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">
              {catalog?.top_categories?.find((c) => c.id === item.top_category)?.label || item.top_category}
              {item.subcategory ? <span> · {item.subcategory.replace(/-/g, " ")}</span> : null}
            </div>
            <h3 className="mt-1 font-serif-display text-3xl truncate">{item.name}</h3>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-lg font-medium">{item.quantity} {item.unit}</span>
              {(item._alerts || []).map((a) => <AlertPill key={a} tag={a} />)}
            </div>
          </div>
          <button onClick={onClose} className="p-1"><X size={18} weight="bold" /></button>
        </div>

        {item.photos?.length > 0 && (
          <div className="mt-4 flex gap-2 overflow-x-auto">
            {item.photos.slice(0, 4).map((p, i) => (
              <img key={i} src={p} alt="" className="h-24 w-24 rounded-lg object-cover shrink-0" />
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-1 border-b border-black/10">
          {["overview", "adjust", "sell", "history", "notes"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              style={tab === t ? { color: accent, borderColor: accent } : undefined}
              className={`px-3 py-2 text-sm capitalize border-b-2 -mb-px ${tab === t ? "" : "border-transparent text-[#3A3830] hover:text-[#1B4332]"}`}
              data-testid={`inv-detail-tab-${t}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "overview" && (
            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <FieldRow label="Location">{locations.find((l) => l.id === item.location_id)?.name || "—"}</FieldRow>
              <FieldRow label="Condition">{item.condition || "—"}</FieldRow>
              <FieldRow label="Ownership">{item.ownership}</FieldRow>
              <FieldRow label="Visibility">{item.visibility}</FieldRow>
              <FieldRow label="Manufacturer">{item.manufacturer || "—"}</FieldRow>
              <FieldRow label="Model">{item.model || "—"}</FieldRow>
              <FieldRow label="Serial number">{item.serial_number || "—"}</FieldRow>
              <FieldRow label="Year">{item.year || "—"}</FieldRow>
              <FieldRow label="Best before">{item.best_before_date || "—"}</FieldRow>
              <FieldRow label="Warranty expires">{item.warranty_expiration || "—"}</FieldRow>
              <FieldRow label="Purchase price">{item.purchase_price ? `${item.currency || "USD"} ${item.purchase_price}` : "—"}</FieldRow>
              <FieldRow label="Reorder / target">
                {item.reorder_level ?? "—"} / {item.target_level ?? "—"}
              </FieldRow>
              {item.description && (
                <div className="md:col-span-2 mt-2 text-sm text-[#3A3830]">{item.description}</div>
              )}
              {item.listed_product_id && (
                <div className="md:col-span-2 mt-2 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                  <Storefront size={12} weight="fill" className="inline mr-1" /> Listed on your shop.
                </div>
              )}
              <div className="md:col-span-2 mt-4">
                <button onClick={archive}
                  className="text-xs text-red-700 hover:underline" data-testid="inv-detail-archive">
                  Archive this item
                </button>
              </div>
            </dl>
          )}

          {tab === "adjust" && (
            <div className="space-y-3">
              <div className="text-sm text-[#3A3830]">
                Adjust the quantity — every change is written to the audit ledger.
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs text-[#3A3830]">Change (positive or negative)</div>
                  <input type="number" step="0.01" value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                    placeholder="e.g. +20 or -5" data-testid="inv-detail-delta" />
                </label>
                <label className="block">
                  <div className="text-xs text-[#3A3830]">Reason</div>
                  <input value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Harvested / Sold / Waste / Damaged…"
                    className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                    data-testid="inv-detail-reason" />
                </label>
              </div>
              <button onClick={adjust} disabled={busy || !delta}
                style={{ background: accent }}
                className="rounded-full text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="inv-detail-adjust">
                {busy ? "Saving…" : "Save adjustment"}
              </button>
            </div>
          )}

          {tab === "sell" && (
            <div className="space-y-3">
              <div className="text-sm text-[#3A3830]">
                {sellTargetLabel} — this item stays connected to your inventory.
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-xs text-[#3A3830]">Price ({item.currency || "USD"} / {item.unit})</div>
                  <input type="number" min="0" step="0.01" value={sellPrice}
                    onChange={(e) => setSellPrice(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                    data-testid="inv-sell-price" />
                </label>
                <label className="block">
                  <div className="text-xs text-[#3A3830]">Product category</div>
                  <select value={sellCat} onChange={(e) => setSellCat(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
                    data-testid="inv-sell-category">
                    {sellCategories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                  </select>
                </label>
              </div>
              <button onClick={sell} disabled={busy || !sellPrice}
                style={{ background: accent }}
                className="rounded-full text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="inv-sell-btn">
                {busy ? "Listing…" : sellTargetLabel}
              </button>
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-1 text-sm">
              {txns.length === 0 && <div className="text-[#3A3830]">No transactions yet.</div>}
              {txns.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 border-b border-black/5 py-2 last:border-0">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-[0.22em]" style={{ color: accent }}>{t.kind}</div>
                    <div className="text-xs text-[#3A3830]">{new Date(t.created_at).toLocaleString()}</div>
                    {t.reason && <div className="text-xs">{t.reason}</div>}
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${t.delta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {t.delta > 0 ? "+" : ""}{t.delta}
                    </div>
                    <div className="text-[10px] text-[#3A3830]">= {t.quantity_after}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "notes" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a dated note…"
                  className="flex-1 rounded-lg border border-black/15 px-3 py-2 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                  data-testid="inv-detail-note-input" />
                <button onClick={addNote} disabled={!note.trim()}
                  className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-3 py-2 text-xs disabled:opacity-50"
                  data-testid="inv-detail-note-save">Add</button>
              </div>
              <div className="space-y-1 text-sm">
                {(item.notes || []).length === 0 && <div className="text-[#3A3830]">No notes yet.</div>}
                {(item.notes || []).slice().reverse().map((n) => (
                  <div key={n.id} className="border-b border-black/5 py-2 last:border-0">
                    <div className="text-xs text-[#3A3830]">{new Date(n.created_at).toLocaleString()} · {n.user}</div>
                    <div>{n.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function FieldLabel({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">{label}</div>
      {hint && <div className="text-[11px] text-[#3A3830]">{hint}</div>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-black/5 py-1.5 last:border-0">
      <dt className="text-xs text-[#3A3830] shrink-0">{label}</dt>
      <dd className="text-sm text-right truncate">{children}</dd>
    </div>
  );
}
